import { Fragment, useMemo } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import {
  GLOSSARY_ALPHABET,
  GLOSSARY_TERMS,
  glossaryTermLabel,
  type GlossaryTerm,
} from "@/data/glossary"
import type { SeoMeta } from "@/types"

function AlphabetNav({ activeLetters }: { activeLetters: Set<string> }) {
  return (
    <nav
      aria-label="Glossary alphabet navigation"
      className="flex flex-wrap justify-center gap-1"
    >
      {GLOSSARY_ALPHABET.map((letter) => {
        const hasTerms = activeLetters.has(letter)
        return hasTerms ? (
          <a
            key={letter}
            href={`#letter-${letter}`}
            className="flex size-9 items-center justify-center rounded-md text-sm font-semibold text-charcoal transition-colors hover:bg-red hover:text-white"
          >
            {letter}
          </a>
        ) : (
          <span
            key={letter}
            className="flex size-9 items-center justify-center rounded-md text-sm font-semibold text-stone"
          >
            {letter}
          </span>
        )
      })}
    </nav>
  )
}

function GlossaryItem({ item }: { item: GlossaryTerm }) {
  return (
    <div id={item.slug} className="scroll-mt-32 py-5">
      <dt className="text-base font-semibold text-charcoal">
        <Link
          href={`/glossary/${item.slug}`}
          className="transition-colors hover:text-red"
        >
          {item.term}
        </Link>
      </dt>
      <dd className="mt-1.5 text-sm leading-relaxed text-slate">
        {item.definition}

        {item.seeAlso && item.seeAlso.length > 0 && (
          <span className="mt-2 block text-xs text-slate/70">
            See also:{" "}
            {item.seeAlso.map((slug, i) => {
              const label = glossaryTermLabel(slug)
              if (!label) return null
              return (
                <Fragment key={slug}>
                  {i > 0 && ", "}
                  <Link
                    href={`/glossary/${slug}`}
                    className="text-red transition-colors hover:text-red/70"
                  >
                    {label}
                  </Link>
                </Fragment>
              )
            })}
          </span>
        )}

        {item.relatedPage && (
          <Link
            href={item.relatedPage.href}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red transition-colors hover:text-red/70"
          >
            {item.relatedPage.label} &rarr;
          </Link>
        )}
      </dd>
    </div>
  )
}

export default function Glossary({ meta }: { meta: SeoMeta }) {
  const grouped = useMemo(() => {
    const groups: Record<string, GlossaryTerm[]> = {}
    for (const term of GLOSSARY_TERMS) {
      const letter = term.term[0].toUpperCase()
      if (!groups[letter]) groups[letter] = []
      groups[letter].push(term)
    }
    return groups
  }, [])

  const activeLetters = useMemo(
    () => new Set(Object.keys(grouped)),
    [grouped],
  )

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            name: "Property Marketing Glossary",
            description: meta.description,
            url: meta.canonical,
            hasDefinedTerm: GLOSSARY_TERMS.map((item) => ({
              "@type": "DefinedTerm",
              name: item.term,
              description: item.definition,
              url: `${new URL(meta.canonical).origin}/glossary/${item.slug}`,
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Glossary" }]} />
      </Head>

      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Glossary
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Property marketing, explained
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Every term estate agents and property professionals need to know,
            from asking price to Zoopla, with everything in between.
          </p>
        </div>
      </section>

      <section className="sticky top-16 z-10 border-y border-stone bg-white/95 px-6 py-3 backdrop-blur-sm">
        <div className="mx-auto max-w-3xl">
          <AlphabetNav activeLetters={activeLetters} />
        </div>
      </section>

      <section className="px-6 py-20">
        <div className="mx-auto max-w-3xl space-y-12">
          {GLOSSARY_ALPHABET.filter((letter) => grouped[letter]).map(
            (letter) => (
              <ScrollReveal key={letter}>
                <div id={`letter-${letter}`} className="scroll-mt-32">
                  <h2 className="font-display text-2xl font-bold text-charcoal">
                    {letter}
                    <span className="text-red">.</span>
                  </h2>
                  <dl className="mt-2 divide-y divide-stone/50">
                    {grouped[letter].map((item) => (
                      <GlossaryItem key={item.slug} item={item} />
                    ))}
                  </dl>
                </div>
              </ScrollReveal>
            ),
          )}
        </div>
      </section>

      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Ready to put these terms into practice
              <span className="text-red">?</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Turn your property listings into scroll-stopping video ads. It
              takes 60 seconds.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
